import { useId, useMemo, useRef, useState } from 'react'
import { formatMetricValue, formatTime } from '../lib/format'

/** Ein Messpunkt. Min und Max stehen nur an verdichteten Punkten. */
export interface Punkt {
  time: number
  value: number
  min?: number
  max?: number
}

export interface Series {
  /** Eindeutiger Schlüssel, bestimmt auch die Farbe. */
  key: string
  /** Beschriftung in Legende und an der Linie. */
  label: string
  points: Punkt[]
  /** Metrik dieser Reihe; ohne sie gilt die des Diagramms. */
  metric?: string
  /**
   * Achse dieser Reihe. Eine zweite Achse gibt es nur für eine andere
   * Einheit (docs/design/live-verlauf.md, Punkt 8): Prozent und Mbit/s in
   * einer Skala wäre eine Linie, die immer am Boden klebt.
   */
  achse?: 'links' | 'rechts'
}

/** Ein Alarm oder ein Wartungsfenster im Graphen (Punkt 11). */
export interface Markierung {
  kind: 'alarm' | 'wartung'
  title: string
  severity?: string
  from: number
  to?: number
}

interface LineChartProps {
  series: Series[]
  /** Metrikschlüssel, bestimmt die Formatierung der Werte. */
  metric: string
  height?: number
  /** Untere Grenze der Werteachse, falls die Daten sie nicht vorgeben. */
  minValue?: number
  maxValue?: number
  /** Alarme und Wartungsfenster, die im Graphen markiert werden. */
  marker?: Markierung[]
  /**
   * Ziehen wählt einen Bereich. Ohne Rückruf bleibt das Diagramm, wie es
   * war — das Zoomen ist eine Sache der Seite, nicht des Diagramms.
   */
  onZoom?: (from: number, to: number) => void
}

// Farben werden in fester Reihenfolge vergeben und nie im Kreis wiederverwendet
// (docs/design/diagrammfarben.md).
const SERIES_COLORS = ['var(--chart-1)', 'var(--chart-2)', 'var(--chart-3)', 'var(--chart-4)']
const MAX_SERIES = SERIES_COLORS.length

const PADDING = { top: 16, right: 92, bottom: 26, left: 52 }

/**
 * Liniendiagramm für Messreihen über die Zeit. Eine einzige Werteachse, dünne
 * Linien, zurückhaltendes Raster, Fadenkreuz mit Werteanzeige und – bei bis zu
 * vier Reihen – eine Beschriftung direkt an der Linie.
 */
export function LineChart({
  series,
  metric,
  height = 220,
  minValue,
  maxValue,
  marker,
  onZoom,
}: LineChartProps) {
  const clipId = useId()
  const svgRef = useRef<SVGSVGElement>(null)
  const [hoverX, setHoverX] = useState<number | null>(null)
  const [width, setWidth] = useState(720)
  // Das Ziehen zum Zoomen: Anfang und aktueller Punkt in Diagramm-Koordinaten.
  const [zug, setZug] = useState<{ von: number; bis: number } | null>(null)

  const visible = series.slice(0, MAX_SERIES)
  const rechts = visible.filter((s) => s.achse === 'rechts')
  const links = visible.filter((s) => s.achse !== 'rechts')

  const scale = useMemo(() => {
    const allPoints = visible.flatMap((s) => s.points)
    if (allPoints.length === 0) return null

    const times = allPoints.map((p) => p.time)
    const tMin = Math.min(...times)
    const tMax = Math.max(...times)

    const innerW = Math.max(1, width - PADDING.left - PADDING.right)
    const innerH = Math.max(1, height - PADDING.top - PADDING.bottom)

    // Je Achse eine eigene Spanne. Ein verdichteter Punkt spannt bis an
    // seine Ausschläge, sonst ragte die Fläche aus dem Bild.
    const spanne = (reihen: Series[], unten?: number, oben?: number) => {
      const werte = reihen.flatMap((s) =>
        s.points.flatMap((p) => [p.value, p.min ?? p.value, p.max ?? p.value]),
      )
      let vMin = unten ?? (werte.length > 0 ? Math.min(...werte) : 0)
      let vMax = oben ?? (werte.length > 0 ? Math.max(...werte) : 1)
      if (vMin === vMax) {
        // Eine völlig flache Reihe bekommt trotzdem eine sichtbare Höhe.
        vMin -= 1
        vMax += 1
      } else {
        const margin = (vMax - vMin) * 0.08
        vMin -= margin
        vMax += margin
      }
      return {
        vMin,
        vMax,
        y: (v: number) => PADDING.top + innerH - ((v - vMin) / (vMax - vMin)) * innerH,
      }
    }

    const achseLinks = spanne(links.length > 0 ? links : visible, minValue, maxValue)
    const achseRechts = rechts.length > 0 ? spanne(rechts) : null

    return {
      tMin,
      tMax,
      vMin: achseLinks.vMin,
      vMax: achseLinks.vMax,
      x: (t: number) => PADDING.left + (tMax === tMin ? innerW / 2 : ((t - tMin) / (tMax - tMin)) * innerW),
      y: achseLinks.y,
      rechtsY: achseRechts?.y,
      rechtsMin: achseRechts?.vMin,
      rechtsMax: achseRechts?.vMax,
      innerW,
      innerH,
    }
  }, [visible, links, rechts, width, height, minValue, maxValue])

  if (!scale) {
    return (
      <div className="empty" style={{ height }}>
        <span>Für diesen Zeitraum liegen keine Messwerte vor.</span>
      </div>
    )
  }

  const ticks = valueTicks(scale.vMin, scale.vMax)
  const rechtsTicks =
    scale.rechtsMin !== undefined && scale.rechtsMax !== undefined
      ? valueTicks(scale.rechtsMin, scale.rechtsMax)
      : []
  const hoverTime = hoverX === null ? null : invertX(hoverX, scale)
  /** Die y-Funktion dieser Reihe: linke oder rechte Achse. */
  const yFor = (s: Series) => (s.achse === 'rechts' && scale.rechtsY ? scale.rechtsY : scale.y)
  const metricOf = (s: Series) => s.metric ?? metric
  const zugVon = zug ? Math.min(zug.von, zug.bis) : 0
  const zugBreite = zug ? Math.abs(zug.bis - zug.von) : 0

  return (
    <figure style={{ margin: 0 }}>
      <svg
        ref={svgRef}
        role="img"
        aria-label={`Verlauf ${visible.map((s) => s.label).join(', ')}`}
        viewBox={`0 0 ${width} ${height}`}
        onPointerMove={(e) => {
          const rect = e.currentTarget.getBoundingClientRect()
          if (rect.width > 0 && Math.abs(rect.width - width) > 8) setWidth(Math.round(rect.width))
          const ratio = width / (rect.width || width)
          const x = (e.clientX - rect.left) * ratio
          setHoverX(x)
          setZug((z) => (z ? { ...z, bis: x } : z))
        }}
        onPointerLeave={() => {
          setHoverX(null)
          setZug(null)
        }}
        onPointerDown={(e) => {
          if (!onZoom) return
          const rect = e.currentTarget.getBoundingClientRect()
          const ratio = width / (rect.width || width)
          const x = (e.clientX - rect.left) * ratio
          setZug({ von: x, bis: x })
        }}
        onPointerUp={() => {
          if (!onZoom || !zug) return
          setZug(null)
          // Ein Klick ist kein Bereich: unter zehn Bildpunkten passiert nichts.
          if (Math.abs(zug.bis - zug.von) < 10) return
          const a = invertX(Math.min(zug.von, zug.bis), scale)
          const b = invertX(Math.max(zug.von, zug.bis), scale)
          onZoom(a, b)
        }}
        style={{
          width: '100%',
          height,
          display: 'block',
          ...(onZoom ? { cursor: 'crosshair', touchAction: 'none' } : {}),
        }}
      >
        <defs>
          <clipPath id={clipId}>
            <rect x={PADDING.left} y={PADDING.top} width={scale.innerW} height={scale.innerH} />
          </clipPath>
        </defs>

        {/* Raster und Achsenbeschriftung bleiben zurückhaltend. */}
        {ticks.map((t) => (
          <g key={t}>
            <line
              x1={PADDING.left}
              x2={PADDING.left + scale.innerW}
              y1={scale.y(t)}
              y2={scale.y(t)}
              stroke="var(--chart-grid)"
              strokeWidth="1"
            />
            <text
              x={PADDING.left - 8}
              y={scale.y(t)}
              textAnchor="end"
              dominantBaseline="middle"
              fontSize="11"
              fill="var(--muted)"
              fontFamily="var(--font-mono)"
            >
              {formatMetricValue(metric, t)}
            </text>
          </g>
        ))}

        <text x={PADDING.left} y={height - 8} fontSize="11" fill="var(--muted)" fontFamily="var(--font-mono)">
          {formatTime(new Date(scale.tMin).toISOString())}
        </text>
        <text
          x={PADDING.left + scale.innerW}
          y={height - 8}
          textAnchor="end"
          fontSize="11"
          fill="var(--muted)"
          fontFamily="var(--font-mono)"
        >
          {formatTime(new Date(scale.tMax).toISOString())}
        </text>

        {/* Zweite Achse rechts, nur wenn eine Reihe sie braucht. */}
        {rechtsTicks.map((t) => (
          <text
            key={`rechts-${t}`}
            x={PADDING.left + scale.innerW + 8}
            y={scale.rechtsY ? scale.rechtsY(t) : 0}
            dominantBaseline="middle"
            fontSize="11"
            fill="var(--muted)"
            fontFamily="var(--font-mono)"
          >
            {formatMetricValue(metricOf(rechts[0] ?? visible[0]!), t)}
          </text>
        ))}

        {/* Markierungen zuerst, damit die Linien darüber liegen. */}
        <g clipPath={`url(#${clipId})`}>
          {(marker ?? []).map((m, i) => {
            const x1 = scale.x(Math.max(m.from, scale.tMin))
            const farbe = m.kind === 'wartung' ? 'var(--muted)' : 'var(--warn)'
            if (m.to !== undefined) {
              const x2 = scale.x(Math.min(m.to, scale.tMax))
              return (
                <rect
                  key={`m${i}`}
                  x={Math.min(x1, x2)}
                  y={PADDING.top}
                  width={Math.max(1, Math.abs(x2 - x1))}
                  height={scale.innerH}
                  fill={farbe}
                  opacity="0.14"
                >
                  <title>{m.title}</title>
                </rect>
              )
            }
            return (
              <line
                key={`m${i}`}
                x1={x1}
                x2={x1}
                y1={PADDING.top}
                y2={PADDING.top + scale.innerH}
                stroke={farbe}
                strokeWidth="2"
                strokeDasharray="5 4"
              >
                <title>{m.title}</title>
              </line>
            )
          })}

          {/* Verdichtete Reihen bekommen ihre Min/Max-Fläche. Ohne sie
              verschwände eine Spitze von einer Minute im Mittelwert. */}
          {visible.map((s, i) =>
            s.points.some((p) => p.min !== undefined) ? (
              <path
                key={`${s.key}-band`}
                d={bandFor(s.points, scale.x, yFor(s))}
                fill={SERIES_COLORS[i]}
                opacity="0.18"
                stroke="none"
              />
            ) : null,
          )}

          {visible.map((s, i) => (
            <path
              key={s.key}
              d={pathFor(s.points, { x: scale.x, y: yFor(s) })}
              fill="none"
              stroke={SERIES_COLORS[i]}
              strokeWidth="2"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          ))}

          {/* Der gezogene Bereich. */}
          {zug && zugBreite > 2 ? (
            <rect
              x={zugVon}
              y={PADDING.top}
              width={zugBreite}
              height={scale.innerH}
              fill="var(--acc)"
              opacity="0.18"
            />
          ) : null}
        </g>

        {/* Beschriftung direkt an der Linie: Identität hängt nie allein an der Farbe. */}
        {visible.map((s, i) => {
          const last = s.points[s.points.length - 1]
          if (!last) return null
          return (
            <text
              key={`${s.key}-label`}
              x={PADDING.left + scale.innerW + 8}
              y={yFor(s)(last.value)}
              dominantBaseline="middle"
              fontSize="12"
              fill="var(--text)"
              fontFamily="var(--font-mono)"
            >
              <tspan fill={SERIES_COLORS[i]}>●</tspan> {formatMetricValue(metricOf(s), last.value)}
            </text>
          )
        })}

        {hoverTime !== null ? (
          <g>
            <line
              x1={scale.x(hoverTime)}
              x2={scale.x(hoverTime)}
              y1={PADDING.top}
              y2={PADDING.top + scale.innerH}
              stroke="var(--muted)"
              strokeWidth="1"
              strokeDasharray="3 3"
            />
            {visible.map((s, i) => {
              const p = nearestPoint(s.points, hoverTime)
              if (!p) return null
              return (
                <circle
                  key={`${s.key}-hover`}
                  cx={scale.x(p.time)}
                  cy={yFor(s)(p.value)}
                  r="4"
                  fill={SERIES_COLORS[i]}
                  stroke="var(--surf)"
                  strokeWidth="2"
                />
              )
            })}
          </g>
        ) : null}
      </svg>

      {hoverTime !== null ? (
        <figcaption className="row" style={{ gap: 16, fontSize: 12, color: 'var(--muted)', flexWrap: 'wrap' }}>
          <span className="mono">{zeitraumText(visible, hoverTime)}</span>
          {visible.map((s, i) => {
            const p = nearestPoint(s.points, hoverTime)
            if (!p) return null
            const einheit = metricOf(s)
            return (
              <span key={`${s.key}-tip`} className="row" style={{ gap: 6 }}>
                <span className="dot" style={{ background: SERIES_COLORS[i], width: 8, height: 8 }} />
                <span>{s.label}</span>
                <span className="mono" style={{ color: 'var(--text)' }}>
                  {p.min === undefined || p.max === undefined
                    ? formatMetricValue(einheit, p.value)
                    : `Ø ${formatMetricValue(einheit, p.value)} · min ${formatMetricValue(
                        einheit,
                        p.min,
                      )} · max ${formatMetricValue(einheit, p.max)}`}
                </span>
              </span>
            )
          })}
        </figcaption>
      ) : null}

      {visible.length > 1 ? (
        <figcaption className="row" style={{ gap: 14, fontSize: 12, color: 'var(--muted)', marginTop: 4, flexWrap: 'wrap' }}>
          {visible.map((s, i) => (
            <span key={`${s.key}-legend`} className="row" style={{ gap: 6 }}>
              <span className="dot" style={{ background: SERIES_COLORS[i], width: 8, height: 8 }} />
              {s.label}
            </span>
          ))}
        </figcaption>
      ) : null}

      {visible.some((s) => s.points.some((p) => p.min !== undefined)) ? (
        <figcaption style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
          Verdichtet · Linie = Mittelwert, Fläche = kleinster bis größter Wert
          {onZoom ? ' · Bereich ziehen zum Zoomen' : ''}
        </figcaption>
      ) : null}

      {series.length > MAX_SERIES ? (
        <figcaption style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
          Es werden {MAX_SERIES} von {series.length} Reihen gezeigt. Die übrigen stehen in der Tabelle.
        </figcaption>
      ) : null}
    </figure>
  )
}

function pathFor(points: Punkt[], scale: { x: (t: number) => number; y: (v: number) => number }): string {
  return points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${scale.x(p.time).toFixed(1)},${scale.y(p.value).toFixed(1)}`)
    .join(' ')
}

/**
 * Die Fläche zwischen kleinstem und größtem Wert je Punkt: oben hin, unten
 * zurück. Punkte ohne Ausschläge fallen auf ihren Messwert zusammen.
 */
export function bandFor(
  points: Punkt[],
  x: (t: number) => number,
  y: (v: number) => number,
): string {
  if (points.length === 0) return ''
  const hin = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${x(p.time).toFixed(1)},${y(p.max ?? p.value).toFixed(1)}`)
    .join(' ')
  const zurueck = [...points]
    .reverse()
    .map((p) => `L${x(p.time).toFixed(1)},${y(p.min ?? p.value).toFixed(1)}`)
    .join(' ')
  return `${hin} ${zurueck} Z`
}

/**
 * Der Zeitraum, den ein Punkt abdeckt. Bei verdichteten Daten ist das das
 * Intervall bis zum nächsten Punkt, bei rohen Messwerten der Zeitpunkt
 * selbst (docs/design/live-verlauf.md, Punkt 9).
 */
export function zeitraumText(series: Series[], time: number): string {
  for (const s of series) {
    const i = s.points.findIndex((p) => p.time >= time)
    const punkt = i >= 0 ? s.points[i] : s.points[s.points.length - 1]
    if (!punkt) continue
    const naechster = i >= 0 ? s.points[i + 1] : undefined
    const von = formatTime(new Date(punkt.time).toISOString())
    if (punkt.min === undefined || !naechster) return von
    return `${von} – ${formatTime(new Date(naechster.time).toISOString())}`
  }
  return formatTime(new Date(time).toISOString())
}

function invertX(px: number, scale: { tMin: number; tMax: number; innerW: number }): number {
  const ratio = Math.min(1, Math.max(0, (px - PADDING.left) / scale.innerW))
  return scale.tMin + ratio * (scale.tMax - scale.tMin)
}

function nearestPoint(points: Punkt[], time: number): Punkt | null {
  if (points.length === 0) return null
  let best = points[0]!
  let bestDistance = Math.abs(best.time - time)
  for (const p of points) {
    const d = Math.abs(p.time - time)
    if (d < bestDistance) {
      best = p
      bestDistance = d
    }
  }
  return best
}

/** Vier bis fünf runde Werte auf der Achse. */
export function valueTicks(min: number, max: number, count = 4): number[] {
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) return []
  const rawStep = (max - min) / count
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)))
  const normalized = rawStep / magnitude
  const niceStep = (normalized >= 5 ? 5 : normalized >= 2 ? 2 : 1) * magnitude
  const first = Math.ceil(min / niceStep) * niceStep
  const out: number[] = []
  for (let v = first; v <= max + niceStep * 0.001; v += niceStep) {
    out.push(Number(v.toFixed(10)))
  }
  return out
}
